import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GetProfileDetails,
  EditProfile,
} from "../../../redux/action/Profile/AdminProfileAction";
import {
  TextField,
  Button,
  Paper,
  IconButton,
  Modal,
  Box,
} from "@mui/material";
import SuccessBackground from "../../../asesst/SuccessBackground.svg";
import Tick from "../../../asesst/tick.svg";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";
import { ImageBaseUrl } from "../../utils/helperData";
import { RxCrossCircled } from "react-icons/rx";

const AdminProfile = () => {
  const dispatch = useDispatch();
  const { adminprofileDetails } = useSelector((state) => state.profile);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    file: null,
  });
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (adminprofileDetails?.result) {
      setFormData({
        first_name: adminprofileDetails.result.first_name || "",
        last_name: adminprofileDetails.result.last_name || "",
        mobile: adminprofileDetails.result.mobile || "",
        file: null,
      });
    }
  }, [adminprofileDetails]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required.";
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      return; 
    }
    const profileData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) {
        profileData.append(key, formData[key]);
      }
    });

    try {
      const result = await dispatch(EditProfile(profileData));
      if (result.success) {
        await dispatch(GetProfileDetails());
        setIsEditing(false);
        setShowAlert(true);
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.error("Unexpected error updating profile:", error);
    }
  };

  const handleCancelClick = () => {
    setFormData({
      first_name: adminprofileDetails?.result?.first_name || "",
      last_name: adminprofileDetails?.result?.last_name || "",
      mobile: adminprofileDetails?.result?.mobile || "",
      file: null,
    });
    setPreview(null);
    setIsEditing(false);
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <Paper className="md:col-span-1 p-6 text-center transform transition-all duration-300 hover:shadow-xl">
          <div className="relative inline-block">
            <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-white shadow-lg">
              <img
                src={
                  preview ||
                  (adminprofileDetails?.result?.profile_pic
                    ? `${ImageBaseUrl}${adminprofileDetails.result.profile_pic}`
                    : "https://cdn-icons-png.flaticon.com/512/6858/6858504.png")
                }
                alt="Profile"
                className="w-full h-full object-cover"
              />
            </div>
            {isEditing && (
              <div className="absolute bottom-0 right-0">
                <input
                  accept="image/*"
                  className="hidden"
                  id="icon-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="icon-button-file">
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="span"
                    className="bg-white shadow-md hover:bg-gray-100"
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
              </div>
            )}
          </div>

          <h2 className="mt-4 text-2xl font-bold text-gray-800">
            {formData.first_name} {formData.last_name}
          </h2>
          <p className="text-gray-600 mt-2 mb-4">
            {adminprofileDetails?.result?.email}
          </p>

          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              className="mt-6"
            >
              Edit Profile
            </Button>
          ) : null}
        </Paper>

        <Paper className="md:col-span-2 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Profile Details
          </h2>

          {errorMessage && (
            <div className="mb-4 p-4 bg-red-100 text-red-700 border border-red-400 rounded">
              {errorMessage}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              error={!!errors.first_name}
              helperText={errors.first_name}
              disabled={!isEditing}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              error={!!errors.last_name}
              helperText={errors.last_name}
              disabled={!isEditing}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Email"
              value={adminprofileDetails?.result?.email || ""}
              disabled
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              error={!!errors.mobile}
              helperText={errors.mobile}
              disabled={!isEditing}
              fullWidth
              variant="outlined"
            />
          </div>

          {isEditing && (
            <div className="flex justify-end space-x-4 mt-8">
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveClick}
              >
                Save Changes
              </Button>
            </div>
          )}
        </Paper>
      </div>

      {showAlert && (
        <Modal open={showAlert} className="justify-center items-center ">
          <Box className="bg-white text-center p-6 rounded-2xl shadow-md max-w-lg mx-auto my-20">
            <RxCrossCircled
              onClick={() => setShowAlert(false)}
              className="float-end top-2 h-6 w-6 cursor-pointer"
            />
            <div className="text-center inline-flex mt-8">
              <div
                className="w-[72px] h-[72px] flex items-center justify-center"
                style={{ backgroundImage: `url(${SuccessBackground})` }}
              >
                <img src={Tick} alt="Success" className="w-7 h-7" />
              </div>
            </div>
            <p className="text-3xl text-[#0B0B0B] font-semibold mt-4 mb-4">
              Success
            </p>
            <span className="text-[#6D6D6D]">
              Profile Updated Successfully
            </span>
            <div className="mt-6 flex justify-center gap-4">
              <button
                onClick={() => setShowAlert(false)}
                className="border border-inherit px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default AdminProfile;